<template>
  <div class="leadSettingsV2">
    <v-row class="mb-6" align="center">
      <v-col cols="12" md="4">
        <v-text-field
          dark
          v-model="search"
          append-icon="mdi-magnify"
          label="Formulare durchsuchen..."
          single-line
          hide-details
          filled
          rounded
          dense></v-text-field>
      </v-col>
      <v-col cols="12" md="8" class="d-flex justify-end align-center">
        <v-switch dark v-model="showArchived" label="Archivierte anzeigen" class="mt-0 mr-6 pt-0" hide-details></v-switch>

        <v-btn color="secondary" outlined class="mr-4" @click="runArchiveScript" :loading="scriptLoading">
          <v-icon left>mdi-broom</v-icon> Auto-Clean (6 Mte)
        </v-btn>

        <v-btn color="primary" @click="openCreateDialog"> <v-icon left>mdi-plus</v-icon> Neues Formular </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items-per-page="-1"
      dark
      :headers="headers"
      :items="filteredLeads"
      :search="search"
      :custom-filter="customSearch"
      :loading="loading"
      class="elevation-2"
      sort-by="createdAt"
      sort-desc>
      <template v-slot:item.title="{ item }">
        <div class="font-weight-bold">{{ item.title }}</div>
        <div class="text-caption" v-if="item.hideInOverview">Archiviert</div>
      </template>

      <template v-slot:item.link="{ item }">
        <v-chip small color="primary" outlined @click="copyText(item.link)" style="cursor: pointer">
          /l/{{ item.link }}
          <v-icon right small>mdi-content-copy</v-icon>
        </v-chip>
      </template>

      <template v-slot:item.stats="{ item }">
        <div class="d-flex align-center">
          <v-badge color="info" :content="item.leadCount ? item.leadCount.toString() : '0'" inline></v-badge>
          <span class="text-caption ml-2" v-if="item.lastLeadDate"> Letzter: {{ item.lastLeadDate | moment("DD.MM.YY") }} </span>
          <span class="text-caption ml-2" v-else>Keine Leads</span>
        </div>
      </template>

      <template v-slot:item.createdAt="{ item }">
        <div class="d-flex align-center">
          <span class="text-caption ml-2" v-if="item.createdAt"> {{ item.createdAt | moment("DD.MM.YY") }} </span>
        </div>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small @click="copyText(item._id)" v-bind="attrs" v-on="on" class="mr-1">
              <v-icon small color="grey">mdi-identifier</v-icon>
            </v-btn>
          </template>
          <span>ObjectId kopieren</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small @click="openInfoDialog(item)" v-bind="attrs" v-on="on" class="mr-1">
              <v-icon small color="blue">mdi-link-variant</v-icon>
            </v-btn>
          </template>
          <span>Webhooks & QR-Codes</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small @click="toggleArchive(item)" v-bind="attrs" v-on="on" class="mr-1">
              <v-icon small :color="item.hideInOverview ? 'green' : 'orange'">
                {{ item.hideInOverview ? "mdi-eye" : "mdi-archive" }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ item.hideInOverview ? "Wiederherstellen" : "Archivieren" }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small color="primary" @click="editItem(item)" v-bind="attrs" v-on="on">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Bearbeiten</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card dark>
        <v-card-title class="headline">
          {{ editedItem._id ? "Formular bearbeiten" : "Neues Formular" }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field v-model="editedItem.title" label="Titel" filled class="mb-2"></v-text-field>
          <v-text-field
            v-model="editedItem.link"
            label="Link (Identifier)"
            hint="Keine Leerzeichen, nur Kleinbuchstaben"
            filled
            @keydown.space.prevent></v-text-field>
          <v-alert type="info" text dense v-if="!editedItem._id" class="mt-4">
            Alle Studios sowie die Standard-Statuswerte werden nach dem Speichern automatisch zugewiesen.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialog = false">Abbrechen</v-btn>
          <v-btn color="primary" :loading="saveLoading" @click="saveLead">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="infoDialog" max-width="600px">
      <v-card dark v-if="activeInfoItem">
        <v-card-title>
          Webhooks & Integration: {{ activeInfoItem.title }}
          <v-spacer></v-spacer>
          <v-btn icon @click="infoDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Webhook URL</v-list-item-subtitle>
                <v-list-item-title class="mt-1"> https://app.wellcomefit.ch/api/websiteleads/{{ activeInfoItem.link }} </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon @click="copyText('https://app.wellcomefit.ch/api/websiteleads/' + activeInfoItem.link)">
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Center Select String (Elementor)</v-list-item-subtitle>
                <v-list-item-title class="text-caption mt-1"> Kopiert den String für das "center" Dropdown. </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon color="primary" @click="copyCenterSelect(activeInfoItem)">
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "../../../api"
// WICHTIG: Ersetze axios/api Aufrufe mit deinen tatsächlichen Helper-Klassen!
// Ich nutze hier Pseudo-API-Aufrufe passend zu deiner Architektur.

export default {
  data() {
    return {
      search: "",
      showArchived: false,
      loading: false,
      saveLoading: false,
      scriptLoading: false,
      dialog: false,
      infoDialog: false,
      leads: [],
      studios: [],
      activeInfoItem: null,
      editedItem: {
        _id: null,
        title: "",
        link: "",
        hideInOverview: false,
      },
      defaultItem: {
        _id: null,
        title: "",
        link: "",
        hideInOverview: false,
      },
      headers: [
        { text: "Titel", value: "title", align: "start" },
        { text: "Link", value: "link" },
        { text: "Performance", value: "stats", sortable: false },
        { text: "Erstellt am", value: "createdAt" },
        { text: "Aktionen", value: "actions", sortable: false, align: "right" },
      ],
    }
  },
  computed: {
    filteredLeads() {
      if (this.showArchived) return this.leads
      return this.leads.filter((lead) => !lead.hideInOverview)
    },
  },
  async created() {
    this.studios = (await api.fetchStudios()) || []
    this.fetchData()
  },
  methods: {
    customSearch(value, search, item) {
      // Wenn das Suchfeld leer ist, zeige alle an
      if (!search) return true

      // Suchbegriff in Kleinbuchstaben umwandeln für bessere Trefferquote
      const term = search.toLowerCase()

      // Prüfen, ob der Begriff im Titel, Link oder der ID steckt
      return (
        (item.title && item.title.toLowerCase().includes(term)) ||
        (item.link && item.link.toLowerCase().includes(term)) ||
        (item._id && item._id.toString().toLowerCase().includes(term))
      )
    },
    async fetchData() {
      this.loading = true
      try {
        // HIER MÜSSTEST DU DEINE API FUNKTION ANPASSEN -> api.fetchLeadSettingsV2()
        const res = await api.fetchLeadSettingsV2()
        this.leads = res
      } catch (error) {
        this.$toast.error("Fehler beim Laden der Formulare")
      }
      this.loading = false
    },
    openCreateDialog() {
      this.editedItem = Object.assign({}, this.defaultItem)
      this.dialog = true
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },
    openInfoDialog(item) {
      this.activeInfoItem = item
      this.infoDialog = true
    },
    // in deinem Vue Frontend in der Funktion saveLead()
    async saveLead() {
      if (!this.editedItem.title || !this.editedItem.link) {
        this.$toast.error("Titel und Link sind Pflichtfelder!")
        return
      }

      this.saveLoading = true
      try {
        await api.saveLeadSettingV2(this.editedItem)
        this.$toast.success("Formular erfolgreich gespeichert")
        this.dialog = false
        this.fetchData()
      } catch (error) {
        // --- NEU: Spezifische Fehlermeldung abgreifen ---
        if (error.response && error.response.data && error.response.data.error) {
          this.$toast.error(error.response.data.error) // Zeigt "Dieser Link ist bereits vergeben..."
        } else {
          this.$toast.error("Fehler beim Speichern")
        }
      }
      this.saveLoading = false
    },
    async toggleArchive(item) {
      const updatedItem = Object.assign({}, item)
      updatedItem.hideInOverview = !updatedItem.hideInOverview

      try {
        await api.saveLeadSettingV2(this.updatedItem)
        const index = this.leads.findIndex((l) => l._id === item._id)
        if (index !== -1) {
          this.$set(this.leads, index, updatedItem)
        }
        this.$toast.success(updatedItem.hideInOverview ? "Archiviert" : "Wiederhergestellt")
      } catch (error) {
        this.$toast.error("Status konnte nicht geändert werden")
      }
    },
    async runArchiveScript() {
      if (!confirm("Dieses Script archiviert alle Formulare, die in den letzten 6 Monaten keine Leads generiert haben. Fortfahren?")) return

      this.scriptLoading = true
      try {
        const res = await api.archiveLeadSettings()
        this.$toast.success(`${res.archivedCount} Formulare erfolgreich aufgeräumt!`)
        this.fetchData()
      } catch (error) {
        this.$toast.error("Fehler beim Ausführen des Scripts")
      }
      this.scriptLoading = false
    },
    copyText(text) {
      if (!text) return
      navigator.clipboard.writeText(text)
      this.$toast.success("Kopiert!")
    },
    copyCenterSelect(item) {
      let text = ""
      // Verwendet die im Formular gespeicherten Studios, falls vorhanden, sonst alle (Fallback)
      const studioList = item.studios && item.studios.length > 0 ? item.studios : this.studios.map((s) => s._id)

      studioList.forEach((studioId, index) => {
        const studioObj = this.studios.find((s) => studioId.toString() === s._id.toString())
        const studioName = studioObj ? studioObj.name : "Unknown Studio"
        text += `${studioName}|${studioId}`
        if (index !== studioList.length - 1) text += "\n"
      })
      this.copyText(text)
    },
  },
}
</script>

<style scoped>
/* Optional: Ein bisschen spacing für die Table-Chips, falls Vuetify das nicht ohnehin sauber löst */
.v-data-table >>> .v-chip {
  font-family: monospace;
}
</style>
